import { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { ticketsAPI } from '@/lib/api';
import { Link2, X, Search } from 'lucide-react';
import { TIPOS_DE_VINCULO, TIPO_POR_OMISION } from '@/lib/tiposDeVinculo';

/** El tope del endpoint. Se declara aca para poder decir "hay mas" en vez de callarlo. */
const LIMITE = 20;

export default function DependencyPicker({
  tenantId,
  capsuleId,
  predecessors,
  onChange,
  excludeTaskId,
  excludeTicketId,
  toKind,
  toId,
  maxItems = 10,
  /** El caso al que pertenece lo que se esta editando. */
  ticketId,
  /**
   * Acota la busqueda a las tareas de **ese** caso.
   *
   * Lo prende el selector de predecesoras de una **tarea**, donde traer tareas de otro caso es
   * casi siempre un error de click. El de un caso no lo prende: un caso esperando a otro caso es
   * legitimo y es la mitad de para que existe esto.
   */
  soloDelTicket = false,
  /**
   * Las tareas del propio caso, para elegir de una lista en vez de adivinar el texto.
   *
   * Las manda quien ya las tiene cargadas, asi que la lista **no cuesta una request**. Sin esto
   * el unico camino era escribir dos letras y esperar: para elegir entre las ocho tareas del caso
   * que se esta mirando, buscar es el gesto equivocado.
   */
  opciones = [],
  /** Deja elegir el tipo de vinculo y la espera por predecesora. */
  conTipoYEspera = false,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!tenantId || !capsuleId || query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await ticketsAPI.searchScheduleEntities(tenantId, capsuleId, {
          q: query.trim(),
          exclude_task_id: excludeTaskId || undefined,
          exclude_ticket_id: excludeTicketId || undefined,
          solo_del_ticket: soloDelTicket && ticketId ? ticketId : undefined,
          limit: LIMITE,
        });
        setResults(res.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, tenantId, capsuleId, excludeTaskId, excludeTicketId, soloDelTicket, ticketId]);

  const yaElegidas = useMemo(
    () => new Set((predecessors || []).map((p) => `${p.kind}:${p.id}`)),
    [predecessors],
  );

  /** Las tareas del caso que todavia se pueden elegir: ni la propia, ni las ya puestas. */
  const disponibles = useMemo(
    () => (opciones || [])
      .filter((t) => t?.task_id && t.task_id !== excludeTaskId && !yaElegidas.has(`task:${t.task_id}`))
      .filter((t) => !(toKind === 'task' && toId === t.task_id)),
    [opciones, excludeTaskId, yaElegidas, toKind, toId],
  );

  const addPredecessor = (item) => {
    if (predecessors.length >= maxItems) return;
    if (toKind === item.kind && toId === item.id) return;
    const key = `${item.kind}:${item.id}`;
    if (yaElegidas.has(key)) return;
    onChange([...predecessors, {
      kind: item.kind,
      id: item.id,
      label: item.label,
      ticket_id: item.ticket_id,
      link_type: TIPO_POR_OMISION,
      lag_dias: 0,
    }]);
    setQuery('');
    setResults([]);
  };

  const cambiar = (idx, campos) => {
    onChange(predecessors.map((p, i) => (i === idx ? { ...p, ...campos } : p)));
  };

  const removePredecessor = (idx) => {
    onChange(predecessors.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid="dependency-picker">
      <div className="space-y-1">
        {predecessors.map((p, i) => (
          <div
            key={`${p.kind}:${p.id}`}
            className="flex items-center gap-1 flex-wrap border border-brand-border rounded-sm px-1.5 py-1 bg-white"
          >
            <Link2 className="w-2.5 h-2.5 text-zinc-400 shrink-0" />
            <span className="text-[9px] text-zinc-400 uppercase shrink-0">
              {p.kind === 'ticket' ? 'Ticket' : 'Tarea'}
            </span>
            <span className="text-[10px] font-medium truncate flex-1 min-w-[80px]">{p.label}</span>
            {conTipoYEspera && (
              <>
                <select
                  value={p.link_type || TIPO_POR_OMISION}
                  onChange={(e) => cambiar(i, { link_type: e.target.value })}
                  className="border border-brand-border rounded-sm h-6 px-1 bg-white text-[9px] shrink-0"
                  title={TIPOS_DE_VINCULO[p.link_type || TIPO_POR_OMISION]?.label}
                  data-testid="dependency-tipo"
                >
                  {Object.entries(TIPOS_DE_VINCULO).map(([id, t]) => (
                    <option key={id} value={id}>{t.sigla}</option>
                  ))}
                </select>
                {/* La espera admite negativo: **es el solape**. "Arranca dos dias antes de que
                    cierre el analisis" es algo que la gente hace igual, y el plan lo tiene que
                    poder decir. */}
                <input
                  type="number"
                  value={p.lag_dias ?? 0}
                  onChange={(e) => cambiar(i, { lag_dias: e.target.value === '' ? 0 : Number(e.target.value) })}
                  className="border border-brand-border rounded-sm h-6 w-12 px-1 bg-white text-[9px] shrink-0"
                  title="Dias de espera. Negativo es solape."
                  data-testid="dependency-lag"
                />
                <span className="text-[9px] text-zinc-400 shrink-0">d</span>
              </>
            )}
            <button
              type="button"
              className="hover:text-brand-danger shrink-0"
              onClick={() => removePredecessor(i)}
              title="Quitar"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        ))}
        {!predecessors.length && (
          <span className="text-[9px] text-zinc-400">Sin predecesores</span>
        )}
      </div>
      {predecessors.length < maxItems && (
        <>
          {disponibles.length > 0 && (
            <select
              value=""
              onChange={(e) => {
                const t = disponibles.find((x) => x.task_id === e.target.value);
                if (t) addPredecessor({ kind: 'task', id: t.task_id, label: t.title || t.task_id, ticket_id: ticketId });
              }}
              className="w-full border border-brand-border rounded-sm h-7 px-1 bg-white text-[10px]"
              data-testid="dependency-select"
            >
              <option value="">Elegir una tarea de este ticket...</option>
              {disponibles.map((t) => (
                <option key={t.task_id} value={t.task_id}>
                  {t.phase ? `[${t.phase}] ` : ''}{t.title || t.task_id}
                </option>
              ))}
            </select>
          )}
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-2 text-zinc-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={soloDelTicket
                ? 'Buscar entre las tareas de este ticket (min. 2 caracteres)...'
                : 'Buscar ticket o tarea (min. 2 caracteres)...'}
              className="h-7 text-[10px] rounded-sm pl-7"
              data-testid="dependency-search-input"
            />
            {results.length > 0 && (
              <div className="absolute z-20 mt-0.5 w-full bg-white border border-brand-border rounded-sm shadow-sm max-h-32 overflow-y-auto">
                {results.map((r) => (
                  <button
                    key={`${r.kind}:${r.id}`}
                    type="button"
                    className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-zinc-50 border-b border-zinc-50 last:border-0"
                    onClick={() => addPredecessor(r)}
                  >
                    <span className="text-zinc-400 uppercase text-[8px]">{r.kind === 'ticket' ? 'Ticket' : 'Tarea'}</span>
                    <div className="font-medium truncate">{r.label}</div>
                    {r.ticket_id && r.kind === 'task' && (
                      <div className="text-zinc-400 font-mono text-[8px]">{r.ticket_id}</div>
                    )}
                  </button>
                ))}
                {/* **El tope se dice.** Una lista cortada en 20 se ve igual que una lista
                    completa, y quien no encuentra lo que busca concluye que no existe. */}
                {results.length >= LIMITE && (
                  <div className="px-2 py-1 text-[9px] text-zinc-500 bg-zinc-50">
                    Hay mas de {LIMITE} coincidencias: afina el texto.
                  </div>
                )}
              </div>
            )}
            {searching && <span className="text-[9px] text-zinc-400">Buscando...</span>}
          </div>
        </>
      )}
    </div>
  );
}
